import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const location = useLocation();

  const category = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = articles.filter((a) => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  const iconMap: Record<string, string> = {
    'celebrity-gossip': 'ri-star-line',
    'exclusive-scoop': 'ri-fire-line',
    'variety-hot': 'ri-tv-2-line',
    'film-tv-news': 'ri-film-line',
    'influencer-news': 'ri-live-line',
  };

  useEffect(() => {
    if (!category) return;
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${category.name} - 51吃瓜官网`,
        description: category.description,
        url: `http://yjpslu.cn/${location.pathname}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '51吃瓜官网',
          url: 'http://yjpslu.cn/',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://yjpslu.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category.name,
            item: `http://yjpslu.cn/category/${slug}`,
          },
        ],
      },
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'category-schema';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    const existing = document.querySelectorAll('script[id="category-schema"]');
    if (existing.length > 1) {
      existing[0].remove();
    }

    return () => {
      const el = document.querySelector('script[id="category-schema"]');
      if (el) document.head.removeChild(el);
    };
  }, [category, slug, location.pathname]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">
              分类未找到
            </h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 text-sm"
            >
              {t('backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav
            className="flex items-center gap-2 text-sm text-foreground-400 mb-6"
            aria-label="Breadcrumb"
          >
            <Link
              to="/"
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('breadcrumbHome')}
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700 whitespace-nowrap">
              {category.name}
            </span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="mb-10">
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm">
                  {category.description}
                </p>
                <p className="text-foreground-400 text-xs mt-1">
                  {categoryArticles.length} {t('articlesCount')}
                </p>
              </div>

              <div className="flex flex-col gap-4" data-product-shop="">
                {categoryArticles.length > 0 ? (
                  categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      {...article}
                      variant="default"
                    />
                  ))
                ) : (
                  <p className="text-foreground-400 text-center py-10">
                    {t('noArticles')}
                  </p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-[260px] flex-shrink-0">
              <div className="sticky top-20">
                <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4 px-1">
                  切换频道
                </h3>
                <div className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-500/10 text-primary-700 font-medium border border-primary-300/40'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      <i
                        className={`${iconMap[cat.slug] || 'ri-folder-line'} text-lg`}
                      />
                      <span className="whitespace-nowrap">{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">
                        {articles.filter((a) => a.categorySlug === cat.slug).length}
                      </span>
                    </Link>
                  ))}
                </div>

                <div className="mt-8 p-4 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-heading font-semibold text-sm text-foreground-800 mb-2">
                    热门标签
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {['顶流男星', '离婚真相', '综艺内幕', '独家爆料', '选角内幕', '网红翻车'].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 rounded-full bg-background-200 text-foreground-600 text-xs whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}